import { useEffect, useState } from 'react';
import Logo from './logo';
import Icon from './icon';
import { readStandId, rememberStand, stands } from '../config/stands';
import { sectionPath } from '../section-url';
import '../styles/panel.scss';

const themes = [
  { id: 'light', label: 'Светлая', icon: 'sun' },
  { id: 'dark', label: 'Тёмная', icon: 'moon' },
  { id: 'system', label: 'Как в системе', icon: 'desktop' },
];

function Shell({
  section,
  sections,
  user,
  showEnvironment,
  theme,
  onTheme,
  collapsed,
  onToggleCollapsed,
  onSection,
  onProfile,
  onSignOut,
  children,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [standOpen, setStandOpen] = useState(false);
  const [pendingStand, setPendingStand] = useState(null);
  const [standId, setStandId] = useState(readStandId);

  useEffect(() => {
    if (theme === 'system') {
      document.documentElement.removeAttribute('data-theme');
    } else {
      document.documentElement.dataset.theme = theme;
    }
  }, [theme]);

  useEffect(() => {
    if (!menuOpen && !standOpen) {
      return undefined;
    }
    function close(event) {
      const target = event.target;
      if (target.closest('.stand') || target.closest('.user-chip') || target.closest('.user-menu')) {
        return;
      }
      setMenuOpen(false);
      setStandOpen(false);
    }
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [menuOpen, standOpen]);

  const stand = stands.find((item) => item.id === standId) || stands[0];

  function renderSection(item) {
    const active = section === item.code;
    return (
      <a
        key={item.code}
        href={sectionPath(item.code)}
        className={active ? 'nav-item is-active' : 'nav-item'}
        aria-current={active ? 'page' : undefined}
        onClick={(event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
          event.preventDefault();
          onSection(item.code);
        }}
      >
        <Icon name={item.icon} size={18} tone="inherit" />
        <span className="nav-item__label">{item.title}</span>
      </a>
    );
  }

  function askStand(next) {
    setStandOpen(false);
    if (next.id === standId) {
      return;
    }
    setPendingStand(next);
  }

  return (
    <div className="shell">
      <header className="topbar">
        <div className="topbar__brand">
          <div className="topbar__logo">
            <Logo />
          </div>
          <button type="button" className="search" disabled>
            <Icon name="search" size={16} />
            <span>Поиск по клиентам, юрлицам и заказам</span>
          </button>
        </div>
        <div className="topbar__tools">
          {showEnvironment ? <div className="stand">
            <button
              type="button"
              className={standOpen ? `env env--${stand.id} is-open` : `env env--${stand.id}`}
              aria-expanded={standOpen}
              aria-haspopup="listbox"
              onClick={() => { setStandOpen((open) => !open); setMenuOpen(false); }}
            >
              <span className={`env__dot env__dot--${stand.id}`} />
              {stand.label}
              <Icon name="env-chevron" size={14} tone="inherit" />
            </button>
            {standOpen ? (
              <div className="stand-menu" role="listbox" aria-label="Стенд">
                {stands.map((item) => {
                  const current = item.id === standId;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="option"
                      aria-selected={current}
                      className={current ? 'stand-menu__item is-current' : 'stand-menu__item'}
                      onClick={() => askStand(item)}
                    >
                      <span className={`env__dot env__dot--${item.id}`} />
                      <span>{item.label}</span>
                      {current ? <Icon name="menu-check" size={12} /> : null}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div> : null}
          <button type="button" className="user-chip" aria-expanded={menuOpen} onClick={() => { setMenuOpen((open) => !open); setStandOpen(false); }}>
            <span className="avatar">{user.initials}</span>
            {user.firstName} {user.lastName}
            <Icon name={menuOpen ? 'chevron-up' : 'chevron-down'} size={14} tone="inherit" />
          </button>
        </div>
      </header>
      {menuOpen ? (
        <div className="user-menu" role="menu">
          <p className="user-menu__role">{user.role}</p>
          <button type="button" className="menu-row" onClick={() => { setMenuOpen(false); onProfile(); }}>
            <Icon name="person" size={16} />
            Профиль
          </button>
          <p className="theme-label">Тема оформления</p>
          <div className="theme-switch" role="radiogroup" aria-label="Тема оформления">
            {themes.map((item) => {
              const active = theme === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={active ? 'theme-switch__item is-active' : 'theme-switch__item'}
                  onClick={() => onTheme(item.id)}
                >
                  <Icon name={item.icon} size={18} tone="inherit" />
                  <span className="theme-switch__label"><span>{item.label}</span></span>
                </button>
              );
            })}
          </div>
          <div className="divider" />
          <button type="button" className="menu-row menu-row--danger" onClick={onSignOut}>
            <Icon name="sign-out" size={16} tone="inherit" />
            Выйти
          </button>
        </div>
      ) : null}
      <div className="shell__body">
        <div className={collapsed ? 'sidebar-slot is-collapsed' : 'sidebar-slot'}>
          <nav className="sidebar" aria-label="Разделы">
            <div className="sidebar__scroll">
              <div className="nav-group">
                <p className="nav-label">РАБОТА</p>
                {sections.work.map(renderSection)}
              </div>
              <div className="nav-group">
                <p className="nav-label">АДМИНИСТРИРОВАНИЕ</p>
                {sections.administration.map(renderSection)}
              </div>
            </div>
          </nav>
          <button type="button" className="sidebar__handle" aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'} onClick={onToggleCollapsed}>
            <Icon name="unfold" size={14} tone="inherit" />
          </button>
        </div>
        {children}
      </div>
      {pendingStand ? (
        <div className="overlay" role="presentation">
          <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="stand-title">
            <div className="dialog__head">
              <h2 id="stand-title"><Icon name="server" size={22} />Перейти на {pendingStand.label}</h2>
              <button type="button" className="icon-button" aria-label="Закрыть" onClick={() => setPendingStand(null)}>
                <Icon name="x" size={18} />
              </button>
            </div>
            <div className="dialog__body">
              <div className="note note--info">
                <Icon name="info" size={20} />
                <p>{pendingStand.label} — отдельная установка панели со своей базой и данными. После перехода откроется этот же раздел, если у вас есть доступ к нему. Несохранённые изменения на {stand.label} будут потеряны.</p>
              </div>
            </div>
            <div className="dialog__footer">
              <button type="button" className="btn btn--secondary btn--small" onClick={() => setPendingStand(null)}>Остаться на {stand.label.toLowerCase()}</button>
              <button type="button" className="btn btn--small" onClick={() => {
                if (pendingStand.href) {
                  const target = new URL(pendingStand.href, window.location.origin);
                  target.pathname = sectionPath(section);
                  window.location.assign(target.toString());
                  return;
                }
                rememberStand(pendingStand.id);
                setStandId(pendingStand.id);
                setPendingStand(null);
              }}>Перейти</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default Shell;
